import { KeyValue } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  effect,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WidgetConfigAccessor } from '../services/widget-config.accessor';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { InputLabelOptions, TypographyKeys } from '@finxone-platform/shared/sys-config-types';
import { Subject, takeUntil } from 'rxjs';
import { InputValidationFactory } from '../input-field-validation/input-validations-factory';
import { ValidationConfig } from '../input-field-validation/validation-config.type';

@Component({
  standalone: false,
  selector: 'finxone-switch',
  templateUrl: './finxone-switch.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinxoneSwitchComponent),
      multi: true,
    },
  ],
})
export class FinxoneSwitchComponent
  extends InputValidationFactory
  implements ControlValueAccessor, OnInit, OnChanges
{
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly configService = inject(WidgetConfigAccessor);

  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  @Input() public label = '';
  @Input() public id = '';
  @Input() public name = '';
  @Input() public value = false;
  @Input() public showErrors = true;
  @Input() public formControlInvalid = false;
  @Input() public validationConfig: ValidationConfig[] = [];
  @Input() public infoText = '';
  @Input() public inputLabelTooltipText = '';
  @Input() public parentControl?: FormControl;
  @Input() public isSmallSwitchButton = true;
  @Input() public toggleActiveBgColour = 'var(--color-primary)';
  @Input() public toggleInactiveBgColour = 'var(--color-border-color)';
  @Input() public isDisabled = false;

  @Output() public switchChanged = new EventEmitter<boolean>();

  public control = new FormControl<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange: Function = () => {};
  // eslint-disable-next-line @typescript-eslint/ban-types
  onTouched: Function = () => {};

  public labelType: InputLabelOptions;
  public labelTypeOptions = InputLabelOptions;
  public textStylingClass: TypographyKeys;
  public tooltipBorderRadiusClass = '';
  public tooltipTextStyleClass = '';

  private cssVars: Record<string, string> = {};

  constructor() {
    super();
    effect(() => {
      const globalCssVariables = this.configService.globalCssVariables();
      if (globalCssVariables) {
        this.cssVars = globalCssVariables;
        this.labelType = this.getCssVariableValue('--inputFieldLabelType') as InputLabelOptions;
        this.textStylingClass = this.getCssVariableValue('--inputFieldTextStylingClass') as TypographyKeys;
        this.tooltipTextStyleClass = this.getCssVariableValue('--tooltipTextStyleClass');
        this.tooltipBorderRadiusClass = this.getCssVariableValue('--tooltipBorderRadiusClass');
      }
    });
  }

  ngOnInit() {
    if (this.parentControl) this.control = this.parentControl as FormControl<boolean>;
    this.control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']?.currentValue !== undefined) {
      this.control.setValue(changes['value'].currentValue);
    }
    if (changes['validationConfig']?.currentValue !== undefined) {
      const validators = this.setupValidation(this.validationConfig);
      this.control.setValidators(validators);
      this.control.updateValueAndValidity();
    }
    if (!this.parentControl) {
      if (changes['formControlInvalid']?.currentValue !== undefined) {
        this.setFormControlValidity(this.formControlInvalid);
      }
    }
  }

  private setFormControlValidity(formControlInvalid: boolean): void {
    if (!formControlInvalid) {
      this.control.markAsPristine();
      this.control.markAsUntouched();
    } else {
      this.control.markAsDirty();
      this.control.markAsTouched();
    }
  }

  public writeValue(value: boolean): void {
    this.value = value ?? false;
    this.control.setValue(this.value, { emitEvent: false, emitModelToViewChange: false });
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    isDisabled ? this.control.disable() : this.control.enable();
  }

  public onSwitchChange(value: boolean): void {
    this.value = value;
    this.control.setValue(value);
    this.onChange(value);
    this.onTouched();
    this.switchChanged.emit(value);
    this.changeDetectorRef.markForCheck();
  }

  private getCssVariableValue(variableName: string): string {
    return this.cssVars[variableName];
  }

  public getErrorMessageBasedOnKey(error: KeyValue<string, any>): string {
    const key = error.key;
    if (!this.validationConfig.some((config) => config.type === key) && typeof error.value === 'string') {
      return error.value;
    }
    const matchingConfig = this.validationConfig.find((config) => config.type === key);
    if (matchingConfig) {
      return this.getErrorMessage(matchingConfig);
    }
    return 'Cannot find error message';
  }
}
